/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";

const painPoints = [
  { title: "New leads are not answered quickly", body: "Inquiries sit in email, forms, and text messages while the buyer moves on to a competitor.", solution: "Lead intake + AI-drafted reply for employee review — sent in minutes, not hours." },
  { title: "Follow-up is inconsistent", body: "Some leads get three touches, some get none, and the owner doesn't know which.", solution: "Automated follow-up reminders + a simple pipeline summary the owner sees daily." },
  { title: "Estimates and proposals take too long", body: "Every proposal is rewritten from scratch, delaying customer decisions.", solution: "Approved templates + AI draft using your language, priced and reviewed by your team." },
  { title: "The owner handles too many repetitive tasks", body: "The business relies on the founder for tasks employees could handle with the right system.", solution: "Documented workflows + AI copilots that let staff act with confidence." },
  { title: "Employees cannot easily find business information", body: "Prices, policies, and product details live in scattered documents and memories.", solution: "An internal knowledge assistant trained on your approved documents." },
  { title: "Customer messages are rewritten from scratch", body: "Support and sales replies eat hours every week.", solution: "Response drafting with your brand voice — reviewed and sent by staff." },
  { title: "Meeting decisions do not become assigned tasks", body: "Great conversations, no follow-through.", solution: "Meeting summaries + auto-extracted action items assigned to the right people." },
  { title: "Reviews are not consistently requested or answered", body: "Reputation is left to chance.", solution: "Review-generation workflow + drafted responses for owner approval." },
  { title: "Training depends on verbal explanations", body: "Every new hire slows the owner down for weeks.", solution: "Written SOPs + an onboarding assistant that answers common questions." },
  { title: "Important workflows exist only in the owner's head", body: "The business cannot scale past the founder.", solution: "Documentation, checklists, and repeatable systems staff can actually run." },
];

const categories = [
  { name: "Sales",         items: ["Lead qualification", "Lead-response drafting", "Appointment follow-up", "Proposal generation", "Pipeline summaries", "Lost-lead reactivation"] },
  { name: "Marketing",     items: ["Social-media content", "Email newsletters", "Review-generation workflows", "Local marketing support", "Promotional campaign drafting", "Content repurposing"] },
  { name: "Customer Service", items: ["Frequently asked questions", "Response drafting", "Appointment reminders", "Service updates", "Review responses", "Customer-feedback analysis"] },
  { name: "Operations",    items: ["Meeting summaries", "Task extraction", "SOP creation", "Internal reporting", "Document drafting", "Scheduling support", "Data-entry assistance"] },
  { name: "Human Resources", items: ["Job descriptions", "Interview guides", "Candidate communication", "Employee onboarding", "Training materials", "Policy explanations"] },
  { name: "Leadership",    items: ["Weekly business summaries", "Decision-support briefs", "Competitor monitoring", "Goal tracking", "Operational dashboards"] },
];

const workflowSteps = [
  { n: "01", label: "Lead enters", body: "From your website, email, form, CRM, or messaging channel." },
  { n: "02", label: "Lead organized", body: "Structured into a single pipeline with the information your team needs." },
  { n: "03", label: "AI drafts response", body: "Using approved business language, tone, and pricing rules." },
  { n: "04", label: "Employee reviews", body: "A human reads the reply, adjusts if needed, and sends." },
  { n: "05", label: "Follow-up created", body: "Automatic reminders for the next touch — nothing forgotten." },
  { n: "06", label: "Owner sees the pipeline", body: "A simple daily summary of what needs attention." },
];

const workflowOutcomes = [
  "Faster response time",
  "Fewer missed leads",
  "More consistent communication",
  "Better follow-up",
  "Less owner dependence",
  "Improved appointment or estimate conversion",
];

const offers = [
  {
    id: "efficiency-review",
    price: "Free",
    name: "AI Efficiency Review",
    body: "A focused 15–20 minute conversation to identify where your business is losing time, leads, consistency, or follow-up.",
    deliverable: "Initial recommendation for the strongest AI opportunity.",
    cta: { label: "Book Free Review", to: "/book-efficiency-review", primary: true },
  },
  {
    id: "assessment",
    price: "$300",
    name: "AI Business Opportunity Assessment",
    body: "A structured review of your workflows, current tools, and readiness for practical AI adoption.",
    deliverables: [
      "Workflow review",
      "AI opportunity map",
      "Top three recommended use cases",
      "Prioritization scorecard",
      "Recommended tools",
      "Risk considerations",
      "Estimated impact",
      "30-day action plan",
    ],
    cta: { label: "Purchase Assessment", to: "/buy-assessment", primary: true },
  },
  {
    id: "sprint",
    price: "Starting at $1,250",
    priceNote: "Typical range: $1,250–$2,500",
    name: "AI Workflow Sprint",
    body: "One workflow designed, installed, and adopted by your team.",
    deliverables: [
      "One workflow designed and implemented",
      "Tool configuration",
      "Prompt library",
      "Testing",
      "Employee training",
      "Standard operating procedure",
      "Owner administration guide",
      "30 days of support",
    ],
    cta: { label: "Discuss a Workflow Sprint", to: "/book-efficiency-review", primary: false },
  },
  {
    id: "monthly",
    price: "Starting at $500/mo",
    name: "Monthly AI Optimization",
    body: "Ongoing workflow monitoring, prompt improvements, and new use-case discovery.",
    deliverables: [
      "Workflow monitoring",
      "Prompt improvements",
      "Automation maintenance",
      "Employee support",
      "Monthly reporting",
      "New use-case recommendations",
      "Quarterly optimization review",
    ],
    cta: { label: "Discuss Monthly Support", to: "/book-efficiency-review", primary: false },
  },
];

const safety = {
  noAuto: ["Hiring", "Firing", "Credit", "Legal decisions", "Medical decisions", "Financial advice", "Insurance eligibility", "Employee discipline"],
  humanReview: ["Customer-facing messages", "Prices", "Contracts", "Proposals", "Legal language", "Financial information", "Health information", "Sensitive employee information"],
};

export default function AIOperations() {
  return (
    <div data-testid="ai-operations-page" className="min-h-screen bg-[#080807] text-[#f4efe6]">
      <Helmet>
        <title>AI Operations Integration — The Entity Agency</title>
        <meta name="description" content="Practical AI systems for small and growing businesses. Faster lead response, less admin work, better follow-up — installed by The Entity Agency." />
        <link rel="canonical" href="https://theentityagency.com/ai-operations" />
      </Helmet>

      {/* ════════ HERO ════════ */}
      <section className="relative overflow-hidden border-b border-[#2a2620]">
        <div aria-hidden className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-[0.08] pointer-events-none"
             style={{ background: "radial-gradient(circle at center, #b8963e 0%, transparent 60%)" }} />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-20 sm:pb-24 relative">
          <p className="overline">AI Operations Integration</p>
          <h1 data-testid="ai-ops-hero-title"
              className="mt-6 font-serif-display tracking-tight text-[36px] sm:text-[54px] lg:text-[72px] leading-[1.02] text-[#f4efe6] max-w-5xl"
              style={{ fontWeight: 600 }}>
            Practical AI systems for <br className="hidden sm:block" />
            <em className="text-[#b8963e] italic font-normal">small and growing businesses.</em>
          </h1>
          <p className="mt-8 text-lg text-[#c9bca4] max-w-3xl leading-relaxed">
            We identify the repetitive work, missed follow-up, slow response times, and information gaps holding your business back — then install simple AI-supported workflows your team can actually use.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/book-efficiency-review" data-testid="ai-ops-primary-cta" className="btn-gold">
              Book a Free AI Efficiency Review <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#offers" data-testid="ai-ops-secondary-cta" className="btn-ghost">
              View service options →
            </a>
          </div>
        </div>
      </section>

      {/* ════════ PAIN POINTS ════════ */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-24">
          <p className="overline">Where is your business losing time or revenue?</p>
          <h2 className="mt-4 font-serif-display text-[32px] sm:text-[44px] leading-[1.08] text-[#f4efe6] max-w-3xl"
              style={{ fontWeight: 600 }}>
            The problems most owners recognize.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-10">
            {painPoints.map((p, i) => (
              <div key={i} className="border-l-2 border-[#2a2620] pl-6" data-testid={`pain-point-${i}`}>
                <h3 className="font-serif-display text-[20px] text-[#f4efe6]" style={{ fontWeight: 600 }}>
                  {p.title}
                </h3>
                <p className="mt-2 text-[#c9bca4] leading-relaxed">{p.body}</p>
                <p className="mt-3 font-mono-eav text-[10px] tracking-[0.2em] uppercase text-[#b8963e]">
                  What we install
                </p>
                <p className="mt-1 text-[#c9bca4]">{p.solution}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ FIRST WORKFLOW ════════ */}
      <section className="border-b border-[#2a2620] bg-[#0e0c0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline">Recommended first workflow</p>
              <h2 className="mt-4 font-serif-display text-[32px] sm:text-[42px] leading-[1.08] text-[#f4efe6]"
                  style={{ fontWeight: 600 }}>
                Lead response and follow-up.
              </h2>
              <p className="mt-6 text-[#c9bca4] leading-relaxed">
                One high-impact workflow that touches nearly every business. A new lead arrives, gets organized, receives a drafted response for employee review, and enters a follow-up cadence — with a simple pipeline summary the owner can see daily.
              </p>
              <p className="mt-4 text-[#c9bca4] leading-relaxed">
                <strong className="text-[#f4efe6]">AI supports the workflow. A human reviews customer-facing messages, pricing, promises, and service commitments.</strong>
              </p>
              <div className="mt-8">
                <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Business outcomes</p>
                <ul className="mt-3 space-y-1.5 text-[#c9bca4]">
                  {workflowOutcomes.map((o, i) => (
                    <li key={i} className="flex gap-3"><span className="text-[#b8963e]">›</span> {o}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid gap-4">
                {workflowSteps.map((s) => (
                  <div key={s.n} className="flex gap-6 items-start border border-[#2a2620] bg-[#0a0908] p-5">
                    <div className="font-serif-display text-3xl text-[#b8963e] leading-none w-14 shrink-0"
                         style={{ fontWeight: 700 }}>{s.n}</div>
                    <div>
                      <div className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#c9bca4]">
                        {s.label}
                      </div>
                      <p className="mt-2 text-[#f4efe6]">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ CATEGORIES ════════ */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-24">
          <p className="overline">Categories of opportunity</p>
          <h2 className="mt-4 font-serif-display text-[32px] sm:text-[44px] leading-[1.08] text-[#f4efe6] max-w-3xl"
              style={{ fontWeight: 600 }}>
            Where AI can help across your business.
          </h2>
          <p className="mt-4 text-[#c9bca4] max-w-3xl">
            Most businesses don't need every one of these. We start with one high-impact workflow, prove it works, and expand from there.
          </p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((cat, i) => (
              <div key={i} className="border border-[#2a2620] bg-[#0e0c0a] p-6" data-testid={`ops-category-${cat.name.toLowerCase().replace(/\s/g,'-')}`}>
                <h3 className="font-serif-display text-[22px] text-[#f4efe6]" style={{ fontWeight: 600 }}>
                  {cat.name}
                </h3>
                <ul className="mt-4 space-y-1.5 text-[15px] text-[#c9bca4]">
                  {cat.items.map((it, j) => (
                    <li key={j} className="flex gap-3"><span className="text-[#b8963e]">›</span> {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ OFFER LADDER ════════ */}
      <section id="offers" className="border-b border-[#2a2620] bg-[#0e0c0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-24">
          <p className="overline">Service ladder</p>
          <h2 className="mt-4 font-serif-display text-[32px] sm:text-[44px] leading-[1.08] text-[#f4efe6] max-w-3xl"
              style={{ fontWeight: 600 }}>
            Start with a conversation. Scale as you see results.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {offers.map((o, i) => (
              <div key={o.id} data-testid={`offer-${o.id}`}
                   className={`border p-8 flex flex-col ${o.cta.primary ? "border-[#b8963e] bg-[#0a0908]" : "border-[#2a2620] bg-[#0a0908]"}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">
                    Offer 0{i + 1}
                  </p>
                  <p className="font-serif-display text-2xl text-[#f4efe6]" style={{ fontWeight: 700 }}>
                    {o.price}
                  </p>
                </div>
                {o.priceNote && (
                  <p className="mt-1 text-right text-xs text-[#8a7e6d]">{o.priceNote}</p>
                )}
                <h3 className="mt-4 font-serif-display text-[24px] text-[#f4efe6]" style={{ fontWeight: 600 }}>
                  {o.name}
                </h3>
                <p className="mt-3 text-[#c9bca4] leading-relaxed">{o.body}</p>
                {o.deliverable && (
                  <p className="mt-4 text-[#c9bca4]"><strong className="text-[#f4efe6]">Deliverable:</strong> {o.deliverable}</p>
                )}
                {o.deliverables && (
                  <ul className="mt-4 space-y-1.5 text-[15px] text-[#c9bca4]">
                    {o.deliverables.map((d, j) => (
                      <li key={j} className="flex gap-3"><span className="text-[#b8963e]">›</span> {d}</li>
                    ))}
                  </ul>
                )}
                <div className="mt-auto pt-8">
                  <Link to={o.cta.to} data-testid={`offer-cta-${o.id}`}
                        className={o.cta.primary ? "btn-gold" : "btn-ghost"}>
                    {o.cta.label} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ SAFETY ════════ */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-24">
          <p className="overline">Human oversight & safety</p>
          <h2 className="mt-4 font-serif-display text-[32px] sm:text-[44px] leading-[1.08] text-[#f4efe6] max-w-3xl"
              style={{ fontWeight: 600 }}>
            AI supports your team. Humans stay in control.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="border border-[#2a2620] bg-[#0e0c0a] p-8">
              <h3 className="font-serif-display text-[22px] text-[#f4efe6]" style={{ fontWeight: 600 }}>
                We do not recommend fully automated decisions for:
              </h3>
              <ul className="mt-4 space-y-1.5 text-[#c9bca4]">
                {safety.noAuto.map((s) => (
                  <li key={s} className="flex gap-3"><span className="text-[#b8963e]">›</span> {s}</li>
                ))}
              </ul>
            </div>
            <div className="border border-[#2a2620] bg-[#0e0c0a] p-8">
              <h3 className="font-serif-display text-[22px] text-[#f4efe6]" style={{ fontWeight: 600 }}>
                Human review is required for:
              </h3>
              <ul className="mt-4 space-y-1.5 text-[#c9bca4]">
                {safety.humanReview.map((s) => (
                  <li key={s} className="flex gap-3"><span className="text-[#b8963e]">›</span> {s}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-8 text-[#8a7e6d] text-sm max-w-3xl leading-relaxed">
            Confidential business, customer, employee, financial, health, or legal information should not be entered into unapproved AI tools. Every workflow we install respects that principle.
          </p>
        </div>
      </section>

      {/* ════════ LOCAL BUSINESS ════════ */}
      <section className="border-b border-[#2a2620] bg-[#0e0c0a]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-20 sm:py-24 text-center">
          <p className="overline">For local & small businesses</p>
          <h2 className="mt-4 font-serif-display text-[30px] sm:text-[42px] leading-[1.08] text-[#f4efe6]"
              style={{ fontWeight: 600 }}>
            AI should help local businesses compete —<br />
            <em className="text-[#b8963e] italic font-normal">not make business more complicated.</em>
          </h2>
          <ul className="mt-10 grid sm:grid-cols-2 gap-3 text-left text-[#c9bca4] max-w-2xl mx-auto">
            {[
              "Use tools the team already understands",
              "Start with one workflow",
              "Keep employees involved",
              "Protect customer relationships",
              "Avoid unnecessary software",
              "Measure business results",
              "Improve the system over time",
            ].map((s, i) => (
              <li key={i} className="flex gap-3 items-start"><span className="text-[#b8963e] mt-0.5">›</span> {s}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ════════ FINAL CTA ════════ */}
      <section>
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-24 text-center">
          <p className="overline">Ready to see where to start?</p>
          <h2 className="mt-4 font-serif-display text-[32px] sm:text-[48px] leading-[1.05] text-[#f4efe6]"
              style={{ fontWeight: 600 }}>
            Book a free 15–20 minute review.
          </h2>
          <p className="mt-6 text-[#c9bca4] max-w-2xl mx-auto">
            No slide decks. No jargon. A focused conversation to identify the strongest first AI workflow for your business.
          </p>
          <div className="mt-10">
            <Link to="/book-efficiency-review" data-testid="ai-ops-footer-cta" className="btn-gold">
              Book Free AI Efficiency Review <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
